/**
 * FaqPage - FAQ knowledge base management
 * Features: Category filter, Add/Edit FAQ, Seed questions
 */

import React, { useState } from 'react';
import { eventService } from '../../services/api';
import toast from 'react-hot-toast';
import DeleteConfirmationModal from '../../components/DeleteConfirmationModal';

// Icon component
const Icon = ({ name, size = 16 }) => {
  const icons = {
    plus: <><path d="M5 12h14M12 5v14"/></>,
    x: <><path d="M18 6 6 18M6 6l12 12"/></>,
    trash: <><path d="M3 6h18M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></>,
    edit: <><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></>,
    sparkles: <><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/></>,
    help: <><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {icons[name] || icons.help}
    </svg>
  );
};

// Modal wrapper
const Modal = ({ isOpen, onClose, title, children, width = 520 }) => {
  if (!isOpen) return null;
  return (
    <div className="modal-overlay" onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 10000
    }}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 16, width, maxWidth: '90vw', maxHeight: '90vh',
        overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
      }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--c-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <Icon name="x" size={20} />
          </button>
        </div>
        <div style={{ padding: 24 }}>{children}</div>
      </div>
    </div>
  );
};

// Form field
const Field = ({ label, children, required }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: 'var(--c-fg-muted)' }}>
      {label} {required && <span style={{ color: 'var(--c-destructive)' }}>*</span>}
    </label>
    {children}
  </div>
);

const inputStyle = {
  width: '100%', padding: '10px 12px', border: '1px solid var(--c-border)',
  borderRadius: 8, fontSize: 14, outline: 'none', fontFamily: 'inherit'
};

// Categories
const CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'venue', label: 'Venue' },
  { id: 'dress', label: 'Dress code' },
  { id: 'food', label: 'Food' },
  { id: 'logistics', label: 'Logistics' },
  { id: 'gifts', label: 'Gifts' },
  { id: 'other', label: 'Other' },
];

// Seed questions for quick setup
const SEED_QUESTIONS = [
  { question: 'What is the dress code?', category: 'dress', answer: 'Please wear traditional attire. Specific dress code details will be shared closer to the event.' },
  { question: 'Is parking available?', category: 'venue', answer: 'Yes, complimentary parking is available at the venue. Valet parking will also be provided.' },
  { question: 'Are gifts expected?', category: 'gifts', answer: 'Your presence is the greatest gift! If you wish to give, monetary gifts are preferred.' },
  { question: 'Can I bring children?', category: 'logistics', answer: 'We love your little ones! Please let us know in your RSVP so we can make arrangements.' },
  { question: 'What time should I arrive?', category: 'logistics', answer: 'Please arrive 30 minutes before the ceremony start time to be seated.' },
  { question: 'Is there accommodation nearby?', category: 'venue', answer: 'Yes, we have arranged special rates at nearby hotels. Details will be shared with confirmed guests.' },
];

// ============ FAQ PAGE ============
function FaqPage({ event, onRefresh, eventId }) {
  const [category, setCategory] = useState('all');
  const [faqModal, setFaqModal] = useState({ open: false, faq: null, index: null });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  // FAQ form state
  const [faqForm, setFaqForm] = useState({
    question: '',
    answer: '',
    category: 'other',
  });

  // Map eventInfo (label/content) to faqs (question/answer) for UI consistency
  const faqs = (event?.eventInfo || []).map(info => ({
    question: info.label || '',
    answer: info.content || '',
    category: info.category || 'other',
    aiGenerated: info.aiGenerated || false,
    _id: info._id,
  }));

  // Filter FAQs by category
  const filteredFaqs = category === 'all' ? faqs : faqs.filter(f => f.category === category);

  // Category counts
  const categoryCounts = CATEGORIES.map(cat => ({
    ...cat,
    count: cat.id === 'all' ? faqs.length : faqs.filter(f => f.category === cat.id).length,
  }));

  // Open FAQ modal
  const openFaqModal = (faq = null, index = null) => {
    if (faq) {
      setFaqForm({
        question: faq.question || '',
        answer: faq.answer || '',
        category: faq.category || 'other',
      });
    } else {
      setFaqForm({ question: '', answer: '', category: 'other' });
    }
    setFaqModal({ open: true, faq, index });
  };

  // Save FAQ
  const saveFaq = async () => {
    if (!faqForm.question || !faqForm.answer) {
      toast.error('Question and answer are required');
      return;
    }
    setSaving(true);
    try {
      // Map FAQ form to event-info API format (label=question, content=answer)
      const apiPayload = {
        label: faqForm.question,
        content: faqForm.answer,
        category: faqForm.category,
      };

      if (faqModal.index !== null) {
        await eventService.updateEventInfo(eventId, faqModal.index, apiPayload);
        toast.success('FAQ updated');
      } else {
        await eventService.addEventInfo(eventId, apiPayload);
        toast.success('FAQ added');
      }
      setFaqModal({ open: false, faq: null, index: null });
      onRefresh();
    } catch (err) {
      toast.error('Failed to save FAQ');
    } finally {
      setSaving(false);
    }
  };

  // Delete FAQ
  const deleteFaq = async () => {
    if (faqModal.index === null) return;
    setSaving(true);
    try {
      await eventService.deleteEventInfo(eventId, faqModal.index);
      toast.success('FAQ deleted');
      setFaqModal({ open: false, faq: null, index: null });
      setDeleteConfirm(false);
      onRefresh();
    } catch (err) {
      toast.error('Failed to delete FAQ');
    } finally {
      setSaving(false);
    }
  };

  // Add seed question
  const addSeedQuestion = (seed) => {
    setFaqForm({
      question: seed.question,
      answer: seed.answer,
      category: seed.category,
    });
    setFaqModal({ open: true, faq: null, index: null });
  };

  return (
    <>
      {/* Stats */}
      <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: 24 }}>
        <div className="stat-card">
          <div className="stat-l">Total FAQs</div>
          <div className="stat-n teal">{faqs.length}</div>
          <div className="stat-foot">Questions answered</div>
        </div>
        <div className="stat-card">
          <div className="stat-l">AI Generated</div>
          <div className="stat-n gold">{faqs.filter(f => f.aiGenerated).length}</div>
          <div className="stat-foot">From conversations</div>
        </div>
        <div className="stat-card">
          <div className="stat-l">Categories</div>
          <div className="stat-n">{new Set(faqs.map(f => f.category)).size}</div>
          <div className="stat-foot">Topics covered</div>
        </div>
      </div>

      <div className="faq-grid" style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 24 }}>
        {/* Category Sidebar */}
        <div className="faq-side">
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--c-fg-muted)', padding: '0 12px 12px' }}>
            Categories
          </div>
          {categoryCounts.map(cat => (
            <div
              key={cat.id}
              className={`faq-cat ${category === cat.id ? 'is-active' : ''}`}
              onClick={() => setCategory(cat.id)}
              style={{
                padding: '10px 12px',
                borderRadius: 8,
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 4,
                background: category === cat.id ? 'hsl(170 60% 25% / 0.08)' : 'transparent',
                color: category === cat.id ? 'var(--c-primary)' : 'var(--c-fg-muted)',
                fontWeight: category === cat.id ? 600 : 400,
              }}
            >
              <span>{cat.label}</span>
              <span style={{ fontSize: 12, opacity: 0.7 }}>{cat.count}</span>
            </div>
          ))}
          <div style={{ borderTop: '1px solid var(--c-border)', margin: '14px 0', paddingTop: 14 }}>
            <button className="btn-primary" style={{ width: '100%', justifyContent: 'center' }} onClick={() => openFaqModal()}>
              <Icon name="plus" size={14} /> Add FAQ
            </button>
          </div>
        </div>

        {/* FAQ List */}
        <div className="faq-list">
          {filteredFaqs.length === 0 ? (
            <div className="panel">
              <div className="panel-body" style={{ padding: 40, textAlign: 'center' }}>
                <Icon name="help" size={48} />
                <h3 style={{ marginTop: 16 }}>No FAQs yet</h3>
                <p style={{ color: 'var(--c-fg-muted)', marginBottom: 24 }}>
                  Add FAQs so AI can automatically answer guest questions.
                </p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
                  {SEED_QUESTIONS.slice(0, 4).map((seed, i) => (
                    <button key={i} className="btn-secondary" onClick={() => addSeedQuestion(seed)}>
                      {seed.question.slice(0, 25)}...
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 16 }}>
              {filteredFaqs.map((faq, i) => {
                const actualIndex = faqs.findIndex(f => f.question === faq.question);
                return (
                  <div key={i} className="panel" style={{ cursor: 'pointer' }} onClick={() => openFaqModal(faq, actualIndex)}>
                    <div className="panel-body" style={{ padding: 20 }}>
                      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: 8,
                          background: faq.aiGenerated ? 'hsl(45 90% 90%)' : 'var(--c-muted)',
                          color: faq.aiGenerated ? 'hsl(45 90% 30%)' : 'var(--c-fg-muted)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                        }}>
                          <Icon name={faq.aiGenerated ? 'sparkles' : 'help'} size={16} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, marginBottom: 8 }}>{faq.question}</div>
                          <div style={{ fontSize: 14, color: 'var(--c-fg-muted)', lineHeight: 1.5 }}>
                            {faq.answer.slice(0, 150)}{faq.answer.length > 150 ? '...' : ''}
                          </div>
                          <div style={{ display: 'flex', gap: 12, marginTop: 12, fontSize: 12, color: 'var(--c-fg-muted)' }}>
                            <span style={{
                              background: 'var(--c-muted)', padding: '2px 8px', borderRadius: 4
                            }}>
                              {CATEGORIES.find(c => c.id === faq.category)?.label || 'Other'}
                            </span>
                            {faq.aiGenerated && <span>AI generated</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* FAQ Modal */}
      <Modal isOpen={faqModal.open} onClose={() => setFaqModal({ open: false, faq: null, index: null })} title={faqModal.faq ? 'Edit FAQ' : 'Add FAQ'}>
        <Field label="Question" required>
          <input
            style={inputStyle}
            value={faqForm.question}
            onChange={e => setFaqForm({...faqForm, question: e.target.value})}
            placeholder="e.g., What is the dress code?"
          />
        </Field>

        <Field label="Answer" required>
          <textarea
            style={{...inputStyle, minHeight: 120, resize: 'vertical'}}
            value={faqForm.answer}
            onChange={e => setFaqForm({...faqForm, answer: e.target.value})}
            placeholder="Type the answer here..."
          />
        </Field>

        <Field label="Category">
          <select style={inputStyle} value={faqForm.category} onChange={e => setFaqForm({...faqForm, category: e.target.value})}>
            {CATEGORIES.filter(c => c.id !== 'all').map(cat => (
              <option key={cat.id} value={cat.id}>{cat.label}</option>
            ))}
          </select>
        </Field>

        {/* Seed suggestions */}
        {!faqModal.faq && (
          <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid var(--c-border)' }}>
            <div style={{ fontSize: 12, color: 'var(--c-fg-muted)', marginBottom: 12 }}>Quick add common questions:</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {SEED_QUESTIONS.map((seed, i) => (
                <button
                  key={i}
                  className="btn-secondary"
                  style={{ fontSize: 12, padding: '4px 10px' }}
                  onClick={() => setFaqForm({ question: seed.question, answer: seed.answer, category: seed.category })}
                >
                  {seed.question.slice(0, 20)}...
                </button>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'space-between' }}>
          <div>
            {faqModal.faq && (
              <button className="btn-secondary" style={{ color: 'var(--c-destructive)' }} onClick={() => setDeleteConfirm(true)} disabled={saving}>
                <Icon name="trash" size={14} /> Delete
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button className="btn-secondary" onClick={() => setFaqModal({ open: false, faq: null, index: null })}>Cancel</button>
            <button className="btn-primary" onClick={saveFaq} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
          </div>
        </div>
      </Modal>

      <DeleteConfirmationModal
        show={deleteConfirm}
        onConfirm={deleteFaq}
        onCancel={() => setDeleteConfirm(false)}
        title="Delete FAQ"
        itemName={faqModal.faq?.question}
        itemType="FAQ"
      />
    </>
  );
}

export default FaqPage;
